import CloseIcon from '@mui/icons-material/Close';
import {
  Box,
  Dialog,
  DialogContent,
  IconButton,
  Link,
} from '@mui/material';
import UseAuthContext from 'context/AuthContext';
import { useSnackbar } from 'notistack';
import { useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ZoneNameFixTable, { formatNameFixCopyLines } from 'components/ZoneNameFixTable';
import { USER_ROLES } from 'utils/constants';
import { ZoneNameFix } from 'utils/idfParse';

export const DESIGNBUILDER_SUPPORT_EMAIL = 'contact@designbuilder.co.il';

export const copyToClipboard = async (text: string): Promise<boolean> => {
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return true;
    }
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.left = '-999999px';
    textArea.style.top = '-999999px';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    const success = document.execCommand('copy');
    document.body.removeChild(textArea);
    return success;
  } catch {
    return false;
  }
};

export const labPrimaryBtnClass =
  'inline-flex items-center justify-center gap-2 w-[300px] h-[50px] px-5 rounded-md text-[16px] font-bold text-white bg-[#0E66F0] hover:bg-[#0B52C0] disabled:opacity-60 disabled:cursor-not-allowed shadow-none border-0 cursor-pointer transition-colors whitespace-nowrap';

export const labSecondaryBtnClass =
  'inline-flex items-center justify-center gap-2 w-[300px] h-[50px] px-5 rounded-md text-[16px] font-bold text-[#0E66F0] bg-white border border-[#0E66F0] hover:bg-[#eef5fb] disabled:opacity-60 disabled:cursor-not-allowed shadow-none cursor-pointer transition-colors whitespace-nowrap';

export const idfErrorDialogTextSx = {
  m: 0,
  p: 0,
  fontSize: '14px',
  lineHeight: 1.5,
  fontWeight: 400,
  color: '#000',
  textAlign: 'start' as const,
};

type IdfParseResultStepProps = {
  open: boolean;
  parseErrors: string[];
  nameFixes?: ZoneNameFix[];
  zoneCount: number;
  isContinuing?: boolean;
  onCancel: () => void;
  onContinue: () => void;
  onFixAndConvert?: () => void;
};

/**
 * Parse result popup after IDF create/parse — Continue / I understand CTA to analysis.
 * Shown for both success and parser errors.
 */
const IdfParseResultStep = ({
  open,
  parseErrors,
  nameFixes = [],
  zoneCount,
  isContinuing = false,
  onCancel,
  onContinue,
  onFixAndConvert,
}: IdfParseResultStepProps) => {
  const { t, i18n } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { role: authorization } = useContext(UseAuthContext);
  const hasParseErrors = parseErrors.length > 0;
  const canContinue = zoneCount > 0;
  const isHebrewUi = Boolean(i18n.language?.startsWith('he'));
  const canSeeNameFixes =
    authorization === USER_ROLES.ADMIN ||
    authorization === USER_ROLES.INSPECTOR ||
    authorization === USER_ROLES.INSPECTOR_MANAGER;
  const visibleNameFixes = useMemo(() => {
    if (!canSeeNameFixes) return [];
    return nameFixes;
  }, [canSeeNameFixes, nameFixes]);
  const specialNameFixes = useMemo(
    () => visibleNameFixes.filter((row) => row.kind !== 'duplicate'),
    [visibleNameFixes]
  );
  const duplicateNameFixes = useMemo(
    () => visibleNameFixes.filter((row) => row.kind === 'duplicate'),
    [visibleNameFixes]
  );
  const needsNameSteps = specialNameFixes.length > 0 && duplicateNameFixes.length > 0;
  const [nameStep, setNameStep] = useState<1 | 2>(1);
  useEffect(() => {
    if (open) setNameStep(1);
  }, [open]);
  const shownNameFixes = needsNameSteps
    ? nameStep === 1
      ? specialNameFixes
      : duplicateNameFixes
    : visibleNameFixes;
  const canFixNames = visibleNameFixes.length > 0;
  const showFixAndConvert = canFixNames && (!needsNameSteps || nameStep === 2);
  const nameFixTitle = t(
    needsNameSteps && nameStep === 2
      ? 'ZONE_NAMES_DUP_TITLE'
      : specialNameFixes.length
        ? 'ZONE_NAMES_SPECIAL_FIX'
        : 'ZONE_NAMES_DUP_TITLE'
  );

  const fullCopyText = useMemo(() => {
    if (!hasParseErrors) return '';
    const lines = parseErrors
      .map((err) => t(err, { defaultValue: err }))
      .filter(Boolean);
    if (shownNameFixes.length) {
      const labelFor = (row: ZoneNameFix) => {
        const label = t(
          row.kind === 'missing'
            ? 'IDF_LAB_NAME_FIX_MISSING'
            : row.kind === 'duplicate'
              ? 'IDF_LAB_NAME_FIX_DUPLICATE'
              : 'IDF_LAB_NAME_FIX_EXTRA'
        );
        return row.kind === 'extra' && row.removeSegs.length
          ? `${label} (${row.removeSegs.join(', ')})`
          : label;
      };
      lines.push('', nameFixTitle);
      lines.push(...formatNameFixCopyLines(shownNameFixes, labelFor));
    }
    return lines.join('\n');
  }, [hasParseErrors, nameFixTitle, parseErrors, shownNameFixes, t]);

  const handleCopyMessage = async () => {
    const success = await copyToClipboard(fullCopyText);
    if (success) {
      enqueueSnackbar(t('REVISION_ERROR_COPY_SUCCESS'), { variant: 'success' });
    } else {
      enqueueSnackbar(t('REVISION_ERROR_COPY_FAILED'), { variant: 'error' });
    }
  };

  return (
    <Dialog
      open={open}
      onClose={isContinuing ? undefined : onCancel}
      maxWidth={false}
      className="idf-parse-result-dialog"
      sx={{
        '& .MuiDialog-paper': {
          position: 'relative',
          borderRadius: '8px',
          width: canFixNames ? 'min(720px, calc(100vw - 32px))' : 'max-content',
          maxWidth: 'calc(100vw - 32px)',
          m: 2,
          overflow: 'visible',
        },
      }}
    >
      <IconButton
        aria-label="close"
        onClick={onCancel}
        disabled={isContinuing}
        sx={{
          position: 'absolute',
          top: 8,
          insetInlineEnd: 8,
          zIndex: 1,
          color: 'grey.500',
        }}
      >
        <CloseIcon />
      </IconButton>

      <DialogContent
        sx={{
          px: 3,
          pt: 5,
          pb: 3,
          overflow: 'visible',
          width: canFixNames ? '100%' : 'max-content',
          maxWidth: '100%',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            gap: 1.5,
            width: canFixNames ? '100%' : 'max-content',
            maxWidth: '100%',
            '& a': {
              fontSize: 'inherit',
              fontWeight: 'inherit',
              lineHeight: 'inherit',
              verticalAlign: 'baseline',
            },
          }}
        >
          {!hasParseErrors && canContinue && (
            <Box
              component="p"
              sx={{ ...idfErrorDialogTextSx, fontWeight: 700, fontSize: '18px' }}
            >
              {t('IDF_LAB_SIMULATE_NOTE')}
            </Box>
          )}

          {(hasParseErrors || zoneCount <= 0) && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
              {hasParseErrors &&
                parseErrors.map((err) => (
                  <Box
                    key={err}
                    component="p"
                    sx={{ ...idfErrorDialogTextSx, fontWeight: 700, fontSize: '18px' }}
                  >
                    {t(err, { defaultValue: err })}
                  </Box>
                ))}
              {zoneCount <= 0 && (
                <Box
                  component="p"
                  sx={{ ...idfErrorDialogTextSx, fontWeight: 700, fontSize: '18px' }}
                >
                  {t('IDF_LAB_NO_ZONES')}
                </Box>
              )}
            </Box>
          )}

          {canFixNames && (
            <ZoneNameFixTable rows={shownNameFixes} title={nameFixTitle} />
          )}

          <Box component="p" sx={{ ...idfErrorDialogTextSx, fontSize: '16px' }}>
            {t('TRAINING_VIDEOS_1')}{' '}
            <Link href="/how-it-works" target="_blank" underline="always">
              {t('TRAINING_VIDEOS_2')}
            </Link>
          </Box>

          {hasParseErrors && (
            <Box
              component="p"
              lang={isHebrewUi ? 'he' : 'en'}
              dir={isHebrewUi ? 'rtl' : 'ltr'}
              sx={{
                ...idfErrorDialogTextSx,
                fontSize: '16px',
                whiteSpace: canFixNames ? 'normal' : 'nowrap',
              }}
            >
              {t('REVISION_ERROR_PARSER_SUPPORT_FOOTER_LINE')}{' '}
              <Link
                href={`mailto:${DESIGNBUILDER_SUPPORT_EMAIL}`}
                underline="always"
              >
                {DESIGNBUILDER_SUPPORT_EMAIL}
              </Link>
            </Box>
          )}

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 1.5,
              width: '100%',
              pt: 1,
            }}
          >
            {needsNameSteps && nameStep === 2 && (
              <button
                type="button"
                className={labSecondaryBtnClass}
                disabled={isContinuing}
                onClick={() => setNameStep(1)}
              >
                {t('ZONE_NAMES_BACK')}
              </button>
            )}
            {hasParseErrors && !showFixAndConvert && (
              <button
                type="button"
                className={labSecondaryBtnClass}
                disabled={isContinuing || !fullCopyText}
                onClick={handleCopyMessage}
              >
                {t('REVISION_ERROR_COPY_BUTTON')}
              </button>
            )}
            <button
              type="button"
              className={labPrimaryBtnClass}
              disabled={isContinuing || (!(needsNameSteps && nameStep === 1) && !canContinue)}
              onClick={
                needsNameSteps && nameStep === 1
                  ? () => setNameStep(2)
                  : showFixAndConvert
                    ? onFixAndConvert || onContinue
                    : onContinue
              }
            >
              {isContinuing ? (
                <span
                  className="h-[18px] w-[18px] shrink-0 animate-spin rounded-full border-2 border-white border-t-transparent"
                  aria-hidden
                />
              ) : null}
              {t(
                needsNameSteps && nameStep === 1
                  ? 'IDF_LAB_NAME_FIX_NEXT'
                  : showFixAndConvert
                    ? 'IDF_LAB_FIX_AND_CONVERT'
                    : hasParseErrors
                      ? 'IDF_LAB_CONTINUE_ACK'
                      : 'IDF_LAB_CONTINUE'
              )}
            </button>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default IdfParseResultStep;
